<script lang="ts">
  import { fly } from 'svelte/transition';
  import { unreadByChat } from '../../chats.svelte';
  import type { ChatItem } from '../../chats.svelte';
  import { showGlobalError } from '../../errorBanner.svelte';
  import type { Palette } from '../../theme.svelte';

  let {
    chat,
    isActive = false,
    isMobile = false,
    palette,
    onOpen,
    onPin,
    onRename,
    onDelete,
  }: {
    chat: ChatItem;
    isActive: boolean;
    isMobile: boolean;
    palette: Palette;
    onOpen: (id: string) => void;
    onPin: (id: string, pinned: boolean) => void;
    onRename: (id: string, newTitle: string) => void;
    onDelete: (id: string) => void;
  } = $props();

  // ── Menu state ──
  let menuOpen = $state(false);
  let menuBtnRef = $state<HTMLButtonElement | null>(null);

  // ── Long-press state (mobile) ──
  let longPressTimer = $state<ReturnType<typeof setTimeout> | null>(null);
  let longPressFired = $state(false);

  // ── Inline rename state ──
  let isRenaming = $state(false);
  let renameValue = $state('');
  let renameInputRef = $state<HTMLInputElement | null>(null);
  let isSaving = $state(false);

  // ── Helpers ──
  function toggleMenu(e: Event) {
    e.stopPropagation();
    menuOpen = !menuOpen;
  }

  function closeMenu() {
    menuOpen = false;
  }

  // Close menu on outside click
  $effect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuBtnRef && !menuBtnRef.contains(e.target as Node)) {
        // Check if click is inside the menu itself
        const menuEl = document.querySelector('.context-menu');
        if (menuEl && menuEl.contains(e.target as Node)) return;
        closeMenu();
      }
    };
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  });

  // ── Long-press detection (mobile) ──
  function handleTouchStart(e: TouchEvent) {
    if (longPressTimer) return;
    longPressFired = false;
    longPressTimer = setTimeout(() => {
      longPressFired = true;
      menuOpen = true;
    }, 500);
  }

  function handleTouchEnd(e: TouchEvent) {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    if (!longPressFired) {
      // Normal tap → open chat
      onOpen(chat.id);
    }
  }

  function handleTouchMove() {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  // ── Pin action ──
  async function handlePin() {
    closeMenu();
    try {
      await onPin(chat.id, !chat.is_pinned);
    } catch (e: any) {
      showGlobalError(e?.message || 'Error al fijar chat');
    }
  }

  // ── Rename action ──
  function startRename() {
    closeMenu();
    isRenaming = true;
    renameValue = chat.title;
    // Focus input after DOM update
    setTimeout(() => {
      renameInputRef?.focus();
      renameInputRef?.select();
    }, 0);
  }

  async function confirmRename() {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === chat.title) {
      cancelRename();
      return;
    }
    isSaving = true;
    try {
      await onRename(chat.id, trimmed);
      isRenaming = false;
      isSaving = false;
    } catch (e: any) {
      showGlobalError(e?.message || 'Error al renombrar chat');
      isSaving = false;
    }
  }

  function cancelRename() {
    isRenaming = false;
    isSaving = false;
    renameValue = '';
  }

  function handleRenameKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmRename();
    } else if (e.key === 'Escape') {
      cancelRename();
    }
  }

  // ── Delete action ──
  function handleDelete() {
    closeMenu();
    onDelete(chat.id);
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="chat-item-wrapper"
  onmouseleave={closeMenu}
  ontouchstart={isMobile ? handleTouchStart : undefined}
  ontouchend={isMobile ? handleTouchEnd : undefined}
  ontouchmove={isMobile ? handleTouchMove : undefined}
>
  {#if isRenaming}
    <input
      bind:this={renameInputRef}
      class="rename-input"
      type="text"
      bind:value={renameValue}
      onkeydown={handleRenameKeydown}
      onblur={confirmRename}
      disabled={isSaving}
      style={`border-color: ${palette.accent}; background-color: ${palette.bgChatInput}; color: ${palette.textPrimary};`}
      maxlength="255"
    />
  {:else}
    <button
      class="recent-chat {isActive ? 'active' : ''}"
      type="button"
      title={chat.title}
      style="color: {palette.textPrimary};"
      onclick={() => onOpen(chat.id)}
    >
      {#if chat.is_pinned}
        <i class="bi bi-pin-angle-fill pin-indicator" style="color: {palette.accent};"></i>
      {/if}
      <span class="chat-title-text">{chat.title}</span>
      {#if unreadByChat[chat.id]}
        <span class="unread-dot"></span>
      {/if}
    </button>

    <!-- 3-dots button -->
    <button
      bind:this={menuBtnRef}
      class="dots-button"
      type="button"
      title="Más opciones"
      onclick={toggleMenu}
      style="color: {palette.icon};"
    >
      <i class="bi bi-three-dots"></i>
    </button>
  {/if}

  <!-- Context menu -->
  {#if menuOpen}
    <div
      class="context-menu"
      transition:fly={{ y: 4, duration: 120 }}
      style={`background-color: ${palette.userMenuBg}; color: ${palette.textPrimary}; border-color: ${palette.borderSoft};`}
    >
      <button
        class="menu-option"
        onclick={handlePin}
        style="color: {palette.textPrimary};"
        onmouseenter={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = palette.userMenuHover}
        onmouseleave={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
      >
        <i class="bi bi-pin-angle-fill"></i>
        {chat.is_pinned ? 'Dejar de fijar chat' : 'Fijar chat'}
      </button>

      <button
        class="menu-option"
        onclick={startRename}
        style="color: {palette.textPrimary};"
        onmouseenter={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = palette.userMenuHover}
        onmouseleave={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
      >
        <i class="bi bi-pencil-fill"></i>
        Renombrar chat
      </button>

      <button
        class="menu-option danger"
        onclick={handleDelete}
        onmouseenter={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
        onmouseleave={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
      >
        <i class="bi bi-trash3-fill"></i>
        Eliminar chat
      </button>
    </div>
  {/if}
</div>

<style>
  .chat-item-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* ── Chat button ── */
  .recent-chat {
    position: relative;
    width: 100%;
    border-radius: 4px;
    border: none;
    background: transparent;
    text-align: left;
    padding: 6px 28px 6px 4px;
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background-color 0.15s ease;
    display: flex;
    align-items: center;
    gap: 3px;
  }

  .recent-chat:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .recent-chat.active {
    background-color: rgba(64, 176, 255, 0.15);
    font-weight: 600;
  }

  .chat-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pin-indicator {
    flex-shrink: 0;
    font-size: 11px;
  }

  .unread-dot {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background-color: #40b0ff;
    flex-shrink: 0;
  }

  /* ── 3-dots button ── */
  .dots-button {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 16px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s ease, background-color 0.15s ease;
    z-index: 2;
  }

  .chat-item-wrapper:hover .dots-button,
  .dots-button:focus-visible {
    opacity: 1;
  }

  .dots-button:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  /* ── Context menu ── */
  .context-menu {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 50;
    border-radius: 8px;
    min-width: 180px;
    border: 1px solid;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    padding: 4px 0;
  }

  .menu-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.1s ease;
  }

  .menu-option i {
    font-size: 14px;
    width: 16px;
    text-align: center;
  }

  .menu-option.danger {
    color: #ef4444 !important;
  }

  .menu-option.danger i {
    color: #ef4444 !important;
  }

  /* ── Inline rename input ── */
  .rename-input {
    width: 100%;
    border: 1px solid;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
  }

  .rename-input:disabled {
    opacity: 0.6;
  }

  /* ── Mobile: always show dots ── */
  @media (max-width: 768px) {
    .dots-button {
      opacity: 0.6;
    }
  }
</style>
